<template>
  <div class="app-shell">
    <!-- Idle timeout warning modal -->
    <Teleport to="body">
      <div v-if="showWarning" class="idle-overlay">
        <div class="idle-modal">
          <div class="idle-modal__icon">
            <i class="material-symbols-outlined">lock_clock</i>
          </div>
          <h3 class="idle-modal__title">{{ $t("Sesi Akan Berakhir") }}</h3>
          <p class="idle-modal__desc">
            {{ $t("Anda tidak aktif. Sesi akan otomatis berakhir dalam") }}
            <span class="idle-modal__countdown">{{ Math.floor(countdown / 60) }}:{{ String(countdown % 60).padStart(2, '0') }}</span>
          </p>
          <div class="idle-modal__actions">
            <button class="idle-modal__btn idle-modal__btn--primary" @click="stayLoggedIn">
              {{ $t("Tetap Login") }}
            </button>
            <button class="idle-modal__btn idle-modal__btn--secondary" @click="doLogout">
              {{ $t("Logout Sekarang") }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Preloader -->
    <div
      v-if="isLoading"
      class="app-preloader"
    >
      <div class="app-preloader__content">
        <i class="material-symbols-outlined app-preloader__icon">sync</i>
        <span class="app-preloader__text">{{ $t("Memuat...") }}</span>
      </div>
    </div>

    <!-- Office Layout (HRD / Inventory) — sidebar terpisah seperti RadFast Billing -->
    <template v-if="isOfficeRoute && !isAuthRoute">
      <OfficeSidebar />
      <TopHeader />
      <div class="main-content flex flex-col min-h-screen" id="main-content">
        <div class="flex-1 p-[12px] sm:p-[16px] md:p-[20px] lg:p-[25px]">
          <router-view :key="$route.path" />
        </div>
        <MainFooter />
      </div>
    </template>

    <!-- Mitra Portal — layout terpisah (sidebar + header sudah include di MitraLayout.vue) -->
    <template v-else-if="isMitraRoute">
      <router-view :key="$route.path" />
    </template>

    <!-- Main Layout (default sidebar) -->
    <template v-else-if="!isAuthRoute && !isPublicRoute">
      <LeftSidebar />
      <TopHeader />
      <div
        class="main-content flex flex-col min-h-screen"
        id="main-content"
      >
        <div class="flex-1 p-[12px] sm:p-[16px] md:p-[20px] lg:p-[25px]">
          <router-view :key="$route.path" />
        </div>
        <MainFooter />
      </div>
    </template>

    <!-- Auth/public pages (no sidebar) -->
    <template v-else>
      <router-view />
    </template>
  </div>
</template>

<script lang="ts">
import { defineAsyncComponent, defineComponent, ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { applyTheme, resolveInitialTheme } from './utils/theme';
import { useIdleTimeout } from './composables/useIdleTimeout';
import { useAuthStore } from './stores/auth';
import { usePermissionStore } from './stores/permissions';
import { fetchAppBranding } from './utils/branding';
import api from './utils/api';

/**
 * Update favicon + title dari branding portal.
 * Fallback ke favicon.ico lokal kalau portal tidak bisa diakses.
 */
async function applyPortalBranding(): Promise<void> {
  try {
    const branding = await fetchAppBranding();

    // Update title
    if (branding.name) {
      document.title = branding.name + ' - Network Management System';
    }

    // Ambil origin portal dari baseURL axios (strip /api suffix)
    const baseURL = String(api.defaults.baseURL ?? '');
    const portalOrigin = baseURL.replace(/\/api\/?$/, '').replace(/\/$/, '');

    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
      ?? (() => {
        const el = document.createElement('link');
        el.rel = 'icon';
        document.head.appendChild(el);
        return el;
      })();

    if (portalOrigin) {
      // Coba favicon.ico dari portal
      const faviconUrl = portalOrigin + '/favicon.ico';
      const img = new Image();
      img.onload = () => { link.href = faviconUrl; };
      img.onerror = () => {
        // Fallback: pakai company_logo sebagai favicon kalau ada dan bukan SVG bundled
        if (branding.logo && branding.logo.startsWith('http')) {
          link.href = branding.logo;
        }
        // else: biarkan favicon.ico lokal (sudah di-set di index.html)
      };
      img.src = faviconUrl;
    } else if (branding.logo && branding.logo.startsWith('http')) {
      // Tidak ada portalOrigin — pakai logo langsung
      link.href = branding.logo;
    }
  } catch {
    // Gagal fetch branding — biarkan favicon lokal
  }
}

const LeftSidebar = defineAsyncComponent(() => import('./components/Layouts/LeftSidebar.vue'));
const OfficeSidebar = defineAsyncComponent(() => import('./components/Layouts/OfficeSidebar.vue'));
const TopHeader = defineAsyncComponent(() => import('./components/Layouts/TopHeader/index.vue'));
const MainFooter = defineAsyncComponent(() => import('./components/Layouts/MainFooter.vue'));

export default defineComponent({
  name: 'App',
  components: {
    LeftSidebar,
    OfficeSidebar,
    TopHeader,
    MainFooter,
  },
  setup() {
    const isLoading = ref(true);
    const route = useRoute();
    const _auth = useAuthStore();
    const permissionStore = usePermissionStore();

    const authRoutes = ['/login', '/forgot-password', '/reset-password'];
    const publicRoutePrefixes = ['/invoice/', '/isolir'];

    // Office routes pakai sidebar terpisah
    const officeRoutePrefixes = ['/hrd', '/office', '/sales', '/inventory', '/finance/collections', '/system', '/accounting'];

    const isAuthRoute = computed(() => authRoutes.includes(route.path));
    const isPublicRoute = computed(() =>
      publicRoutePrefixes.some(prefix => route.path === prefix || route.path.startsWith(prefix))
    );
    const isOfficeRoute = computed(() =>
      officeRoutePrefixes.some(prefix => route.path.startsWith(prefix))
    );
    // Mitra portal pakai layout sendiri (sidebar+header sudah include di MitraLayout.vue)
    // Kecualikan /mitras (admin page) — itu pakai default admin sidebar.
    const isMitraRoute = computed(() =>
      route.path === '/mitra' || route.path.startsWith('/mitra/')
    );

    // Idle timeout — hanya aktif saat login
    const { showWarning, countdown, stayLoggedIn, doLogout } = useIdleTimeout();

    onMounted(() => {
      // Terapkan tema tersimpan (sinkronkan class `dark` di <html> dan
      // `dark-theme` di <body> supaya Tailwind + SCSS template konsisten).
      applyTheme(resolveInitialTheme(), false);

      // Update favicon + title dari branding portal (best-effort, tidak block UI)
      applyPortalBranding();

      // Load permissions jika sudah login
      if (_auth.isAuthenticated) {
        permissionStore.fetchMyPermissions();
      }

      setTimeout(() => {
        isLoading.value = false;
      }, 600);
    });

    return {
      isLoading,
      isAuthRoute,
      isPublicRoute,
      isOfficeRoute,
      isMitraRoute,
      showWarning,
      countdown,
      stayLoggedIn,
      doLogout,
    };
  },
});
</script>

<style lang="postcss">
.app-shell {
  @apply min-h-screen overflow-x-hidden bg-gray-50 text-black transition-colors dark:bg-[#0c1427] dark:text-white;
}

.app-preloader {
  @apply fixed inset-0 z-[9999] flex items-center justify-center bg-white dark:bg-[#0c1427];
}

.app-preloader__content {
  @apply flex flex-col items-center gap-4;
}

.app-preloader__icon {
  @apply animate-spin text-[48px] text-primary-500;
}

.app-preloader__text {
  @apply text-gray-500 dark:text-gray-400;
}

/* Idle timeout warning */
.idle-overlay {
  @apply fixed inset-0 z-[99999] flex items-center justify-center;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(3px);
}

.idle-modal {
  @apply bg-white dark:bg-[#1a2540] rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-4;
  max-width: 360px;
  width: 90%;
}

.idle-modal__icon {
  @apply w-16 h-16 rounded-full flex items-center justify-center;
  background: #fef3c7;
}

.idle-modal__icon i {
  @apply text-[36px];
  color: #d97706;
}

.idle-modal__title {
  @apply text-lg font-bold text-gray-800 dark:text-white text-center;
}

.idle-modal__desc {
  @apply text-sm text-gray-500 dark:text-gray-400 text-center leading-relaxed;
}

.idle-modal__countdown {
  @apply font-bold text-red-500 text-base;
}

.idle-modal__actions {
  @apply flex gap-3 w-full mt-2;
}

.idle-modal__btn {
  @apply flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all;
}

.idle-modal__btn--primary {
  @apply bg-primary-600 text-white hover:bg-primary-700;
  background: #6366f1;
}

.idle-modal__btn--primary:hover {
  background: #4f46e5;
}

.idle-modal__btn--secondary {
  @apply bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300;
}
</style>
